import NiceModal, { useModal } from "@ebay/nice-modal-react";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Button, Fab, IconButton, Stack, Typography } from "@mui/material";
import { SnackbarContext } from "components/context/SnackbarProvider";
import { createContext, useCallback, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import CustomDialog from "./CustomDialog";
import viewFields from "./viewFields";
import ModelInfoButton from "./components/ModelInfoButton";

const ViewContext = createContext({});

export const CustomViewField = ({ field, value, formattedValue }) => {
  if (!viewFields[field.type]) {
    console.warn(
      `No component found for field ${field.name} with type: ${field.type}`,
    );
  }
  let Component =
    viewFields[field.type]?.Component || viewFields["default"].Component;

  return (
    <Component field={field} value={value} formattedValue={formattedValue} />
  );
};

export const CustomViewFields = (props) => {
  const { fields, data } = {
    ...useContext(ViewContext),
    ...props,
  };

  const getValue = (field) => {
    const value = data ? data[field.name] : undefined;
    const formattedValue =
      (field.valueFormatter && field.valueFormatter(value)) || value;
    return { value, formattedValue };
  };

  const field_components = () =>
    fields.map((field, index) => {
      return <CustomViewField key={index} field={field} {...getValue(field)} />;
    });

  if (!data) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6">Loading...</Typography>
      </Stack>
    );
  }

  return <Stack spacing={2}>{field_components()}</Stack>;
};

export const CustomViewActions = (props) => {
  const { onClose } = {
    ...useContext(ViewContext),
    ...props,
  };

  return (
    <>
      <Button onClick={onClose}>Close</Button>
    </>
  );
};

export const CustomViewUpdateFab = (props) => {
  const {
    itemUpdateModal,
    data: { id },
  } = {
    ...useContext(ViewContext),
    ...props,
  };

  const openUpdate = useCallback(() => {
    return NiceModal.show(itemUpdateModal, {
      id,
      onDone: () => {
        NiceModal.hide(itemUpdateModal);
      },
    });
  }, [itemUpdateModal, id]);

  return (
    <Fab
      size="small"
      color="secondary"
      aria-label="add"
      onClick={openUpdate}
      sx={{ position: "fixed", right: 20, bottom: 20 }}
    >
      <ModeEditIcon />
    </Fab>
  );
};

function joinPaths(currentPath, path) {
  if (!path) {
    return;
  }
  if (path.startsWith("/")) {
    return path; // absolute path
  }

  const base = currentPath.replace(/\/+$/, "");
  const relative = path.replace(/^\/+/, "");

  return `${base}/${relative}`;
}

export const CustomViewTitle = (props) => {
  const { title, path, data, updateForm, onClose, modelName } = {
    ...useContext(ViewContext),
    ...props,
  };
  const location = useLocation();
  const absolutePath = joinPaths(location.pathname, path);
  const updateModal = updateForm && useModal(updateForm);
  const { displayError } = useContext(SnackbarContext);

  const openUpdateForm = useCallback(() => {
    NiceModal.show(updateForm, {
      id: data.id,
      obj: data,
      onDone: () => {
        updateModal.hide();
      },
    });
  }, [updateModal, data, displayError]);

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      flexGrow={1}
    >
      <Typography variant="h5" sx={{ ml: 1, mr: "auto" }}>
        {title || "View"}
      </Typography>
      {updateForm && data && (
        <IconButton onClick={openUpdateForm}>
          <EditIcon />
        </IconButton>
      )}
      {path && (
        <IconButton component={Link} to={absolutePath}>
          <OpenInFullIcon />
        </IconButton>
      )}
      <ModelInfoButton modelName={modelName} />
    </Stack>
  );
};

const CustomViewModal = ({ children, ...props }) => {
  return (
    <ViewContext.Provider value={props}>
      {children || (
        <>
          <CustomDialog title>
            <CustomViewTitle />
          </CustomDialog>
          <CustomDialog content>
            <CustomViewFields />
          </CustomDialog>
        </>
      )}
    </ViewContext.Provider>
  );
};

export default CustomViewModal;
